import { useEffect, useRef, useState } from "react";
import ImpressionsController, {
  type impression,
} from "../../assets/Controllers/ImpressionsController";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";

export const useHotelImpressions = () => {
  const [impressions, setImpressions] = useState<impression[]>([]);
  const [sessionId, setSessionId] = useState(uuidv4());
  const processedImpressions = useRef<Set<string>>(new Set());
  const impressionsSpot = useRef<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const location = useLocation();

  useEffect(() => {
    setSessionId(uuidv4());
  }, [location]);

  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const newImpressions: impression[] = [];

        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          const hotelId = el.dataset.hotelId;
          const collectionId = el.dataset.collectionId;
          const bookingId = el.dataset.bookingId;
          if (!hotelId) return;
          if (!collectionId) {
            if (!bookingId) {
              return;
            }
          }

          if (entry.isIntersecting) {
            if (!impressionsSpot.current.has(hotelId)) {
              impressionsSpot.current.add(hotelId);

              const isFirst = !processedImpressions.current.has(hotelId);
              if (isFirst) {
                processedImpressions.current.add(hotelId);
                newImpressions.push({
                  id: collectionId
                    ? collectionId
                    : bookingId
                    ? bookingId
                    : hotelId,
                  isFirst: true,
                  sessionId,
                });
              } else {
                newImpressions.push({
                  id: collectionId
                    ? collectionId
                    : bookingId
                    ? bookingId
                    : hotelId,
                  isFirst: false,
                  sessionId,
                });
              }
            }
          } else {
            impressionsSpot.current.delete(hotelId);
          }
        });

        if (newImpressions.length > 0) {
          setImpressions((prev) => [...prev, ...newImpressions]);
        }
      },
      {
        root: null,
        threshold: 0.5,
      },
    );

    observerRef.current = intersectionObserver;

    // Initial check for already-rendered elements
    const observeHotelCards = () => {
      const elements = document.querySelectorAll(".hotel-result");
      elements.forEach((el) => intersectionObserver.observe(el));
    };

    observeHotelCards();

    // Use MutationObserver to detect when new cards are added
    const mutationObserver = new MutationObserver(() => {
      observeHotelCards();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [sessionId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (impressions.length > 0) {
        ImpressionsController.sendImpressions(impressions);
        setImpressions([]);
      }
    }, 5 * 1000);

    return () => clearInterval(interval);
  }, [impressions]);
};
